import React, { useState, useRef } from 'react';
import { uploadToSupabaseStorage } from '../utils/uploadUtils';

interface ImageUploadFieldProps {
    userId: string;
    currentImageUrl?: string;
    onImageUploaded: (url: string) => void;
    onError: (error: string) => void;
    allowPendingSelection?: boolean;
    onPendingFileSelected?: (file: File | null) => void;
}

export default function ImageUploadField({
    userId,
    currentImageUrl,
    onImageUploaded,
    onError,
    allowPendingSelection = false,
    onPendingFileSelected
}: ImageUploadFieldProps) {
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadsEnabled = !!userId && userId !== 'pending';
    const pendingSelectionEnabled = !uploadsEnabled && allowPendingSelection;

    // Keep preview in sync with prop for when it loads from Firestore
    React.useEffect(() => {
        if (currentImageUrl) {
            setPreviewUrl(currentImageUrl);
        }
    }, [currentImageUrl]);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!uploadsEnabled && !pendingSelectionEnabled) {
            onError('Create your account first, then upload files from your dashboard.');
            return;
        }

        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            onError('Please select a valid image file.');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            onError('Image must be less than 5MB.');
            return;
        }

        // Create preview immediately for better UX
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);

        if (pendingSelectionEnabled) {
            onPendingFileSelected?.(file);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setUploading(true);

        try {
            const publicUrl = await uploadToSupabaseStorage(file, 'teacher-images', userId);

            onImageUploaded(publicUrl);
            setUploading(false);
        } catch (error: any) {
            console.error('Upload error:', error);
            onError('Failed to upload image. ' + (error.message || 'Please try again.'));
            setUploading(false);
            // Revert preview on error if it was a new upload
            if (!currentImageUrl) setPreviewUrl(null);
        }
    };

    const handleRemove = () => {
        setPreviewUrl(null);
        if (pendingSelectionEnabled) {
            onPendingFileSelected?.(null);
        } else {
            onImageUploaded('');
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="image-upload-field" style={{ display: 'grid', gap: '0.75rem', justifyItems: 'start' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>
                Profile Photo (Optional)
            </label>

            <p style={{ margin: '-0.35rem 0 0', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6 }}>
                A clear profile photo helps students trust your listing faster.
            </p>

            {previewUrl ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                        src={previewUrl}
                        alt="Profile preview"
                        style={{
                            width: '128px',
                            height: '128px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '4px solid rgba(255,255,255,0.95)',
                            boxShadow: '0 18px 36px rgba(15,23,42,0.12)'
                        }}
                    />
                    <button
                        type="button"
                        onClick={handleRemove}
                        style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: '#ef4444',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                        }}
                    >
                        ×
                    </button>
                </div>
            ) : (
                <div
                    onClick={() => {
                        if (uploadsEnabled || pendingSelectionEnabled) {
                            fileInputRef.current?.click();
                        }
                    }}
                    style={{
                        width: '128px',
                        height: '128px',
                        borderRadius: '50%',
                        border: '2px dashed #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: uploadsEnabled || pendingSelectionEnabled ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s',
                        background: uploadsEnabled || pendingSelectionEnabled ? 'linear-gradient(180deg, #ffffff, #f8fafc)' : '#f8fafc',
                        boxShadow: '0 12px 28px rgba(15,23,42,0.06)'
                    }}
                    onMouseEnter={(e) => {
                        if (uploadsEnabled || pendingSelectionEnabled) {
                            e.currentTarget.style.borderColor = '#6366f1';
                            e.currentTarget.style.background = '#f8fafc';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.background = uploadsEnabled || pendingSelectionEnabled ? '#fff' : '#f8fafc';
                    }}
                >
                    <span style={{ fontSize: '2rem', color: '#94a3b8', fontWeight: 300 }}>{uploadsEnabled || pendingSelectionEnabled ? '+' : '...'}</span>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                id="profile-photo-upload"
                name="profilePhoto"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {uploading && (
                <div style={{ marginTop: '0.5rem' }}>
                    <div style={{
                        width: '128px',
                        height: '4px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: '50%',
                            background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                            animation: 'indeterminate 1s infinite linear'
                        }} />
                    </div>
                    <style>{`
                        @keyframes indeterminate {
                            0% { left: -50%; width: 50%; }
                            100% { left: 100%; width: 50%; }
                        }
                    `}</style>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                        Uploading to Supabase...
                    </span>
                </div>
            )}

            {pendingSelectionEnabled && previewUrl && !uploading && (
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#047857', lineHeight: 1.6 }}>
                    Image selected. It will upload automatically when your account is created.
                </p>
            )}

            {!uploadsEnabled && !pendingSelectionEnabled && (
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
                    Uploads become available after account creation, when you open your dashboard as an authenticated teacher.
                </p>
            )}
        </div>
    );
}
